/** Small display primitives for the Billing ledger panel. */

import { cn } from '../../lib/utils'

export function Metric({
  label,
  value,
  isDark,
}: {
  label: string
  value: string
  isDark: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50',
      )}
    >
      <div
        className={cn(
          'text-[11px] font-medium uppercase tracking-wider',
          isDark ? 'text-neutral-500' : 'text-neutral-500',
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          'mt-1 text-sm font-semibold',
          isDark ? 'text-neutral-100' : 'text-neutral-900',
        )}
      >
        {value}
      </div>
    </div>
  )
}

export function LedgerList({
  title,
  empty,
  isLoading,
  isDark,
  rows,
}: {
  title: string
  empty: string
  isLoading: boolean
  isDark: boolean
  rows: Array<{ id: string; primary: string; secondary: string; meta: string }>
}) {
  const mutedText = isDark ? 'text-neutral-500' : 'text-neutral-500'
  const strongText = isDark ? 'text-neutral-100' : 'text-neutral-900'

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50',
      )}
    >
      <h3 className={cn('text-xs font-semibold uppercase tracking-wider', mutedText)}>{title}</h3>
      {isLoading ? (
        <p className={cn('mt-3 text-sm', mutedText)}>Loading...</p>
      ) : rows.length === 0 ? (
        <p className={cn('mt-3 text-sm', mutedText)}>{empty}</p>
      ) : (
        <div className="mt-3 divide-y divide-neutral-200 dark:divide-white/[0.06]">
          {rows.map((row) => (
            <div key={row.id} className="grid grid-cols-[1fr_auto] gap-3 py-2 first:pt-0 last:pb-0">
              <div>
                <div className={cn('text-sm font-medium', strongText)}>{row.primary}</div>
                <div className={cn('text-xs', mutedText)}>{row.secondary}</div>
              </div>
              <div className={cn('text-sm font-semibold', strongText)}>{row.meta}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function ReadinessRow({
  label,
  ok,
  isDark,
}: {
  label: string
  ok: boolean
  isDark: boolean
}) {
  return (
    <div className="flex justify-between gap-3">
      <span className={isDark ? 'text-neutral-500' : 'text-neutral-500'}>{label}</span>
      <span className={cn('font-medium', ok ? 'text-emerald-500' : 'text-amber-500')}>
        {ok ? 'ready' : 'not configured'}
      </span>
    </div>
  )
}
